import { Col, Descriptions, Form, Input, Modal, Row, Space, Spin } from 'antd';
import { useCustomMutation, useOne } from '@refinedev/core';
import { IExperienceReservation } from '../../../interfaces/experience';
import { useForm } from '@refinedev/antd';
import { ChildForm } from './ChildForm';

type Props = {
  startId?: string;
  onClose: () => void;
  onStart: () => void;
};

export const EntryDialog = ({ startId, onClose, onStart }: Props) => {
  const { mutate } = useCustomMutation();

  const { data: reservationData } = useOne<IExperienceReservation>({
    resource: 'experienceReservations',
    id: startId,
    meta: {
      fields: [
        'id',
        'status',
        'email',
        'date',
        'startTime',
        'endTime',
        'remark',
        'memo',
        {
          event: ['id', 'name'],
          reservationSlot: ['id', 'plan'],
          user: [
            'id',
            'email',
            {
              profile: [
                'id',
                'name',
                'kana',
                'tel',
                'gender',
                'postalCode',
                'prefecture',
                'city',
                'address',
                'building',
              ],
            },
          ],
          sessions: [
            'id',
            {
              child: ['id'],
            },
          ],
        },
      ],
    },
  });
  const reservation = reservationData?.data;

  const { formProps, autoSaveProps } = useForm({
    resource: 'experienceReservations',
    id: startId,
    action: 'edit',
    autoSave: {
      enabled: true,
      debounce: 500,
    },
    meta: {
      fields: ['id', 'memo'],
    },
    mutationMeta: {
      fields: [{ experienceReservation: ['id', 'memo'] }],
    },
  });

  const handleStart = () => {
    mutate(
      {
        method: 'post',
        url: '',
        values: {},
        meta: {
          operation: 'startExperienceReservation',
          variables: {
            input: {
              type: 'StartExperienceReservationInput!',
              value: {
                where: { id: startId },
                data: {},
              },
            },
          },
          fields: [
            {
              experienceReservation: ['id'],
            },
          ],
        },
      },
      {
        onSuccess: onStart,
      },
    );
  };

  return (
    <Modal open={!!startId} title="受付" onCancel={onClose} onOk={handleStart} width={800}>
      <Row gutter={[16, 16]}>
        {reservation && (
          <>
            <Col xl={24} lg={24} xs={24}>
              <Descriptions
                title="予約"
                bordered
                size="small"
                items={[
                  { key: 'event', label: 'イベント', children: reservation.event.name, span: 2 },
                  { key: 'event', label: 'プラン', children: reservation.reservationSlot.plan },
                  { key: 'date', label: '日付', children: reservation.date },
                  { key: 'start', label: '開始', children: reservation.startTime },
                  { key: 'end', label: '終了', children: reservation.endTime },
                  { key: 'remark', label: '備考', children: reservation.remark, span: 3 },
                ]}
              />
            </Col>
            <Col xl={24} lg={24} xs={24}>
              <Descriptions
                title="予約者"
                bordered
                size="small"
                items={[
                  { key: 'name', label: '氏名', children: reservation.user.profile.name },
                  { key: 'kana', label: 'カナ', children: reservation.user.profile.kana },
                  { key: 'gender', label: '性別', children: reservation.user.profile.gender },
                  {
                    key: 'tel',
                    label: '電話番号',
                    children: reservation.user.profile.tel,
                    span: 3,
                  },
                  {
                    key: 'email',
                    label: 'メールアドレス',
                    children: reservation.user.email,
                    span: 3,
                  },
                  {
                    key: 'postalCode',
                    label: '郵便番号',
                    children: reservation.user.profile.postalCode,
                  },
                  {
                    key: 'prefecture',
                    label: '都道府県',
                    children: reservation.user.profile.prefecture,
                  },
                  { key: 'city', label: '市区町村', children: reservation.user.profile.city },
                  { key: 'address', label: '番地', children: reservation.user.profile.address },
                  { key: 'building', label: 'ビル', children: reservation.user.profile.building },
                ]}
              />
            </Col>
            {reservation.sessions.map((session: any) => (
              <Col xl={24} lg={24} xs={24} key={session.id}>
                <ChildForm childId={session.child.id} />
              </Col>
            ))}
          </>
        )}
        <Col xl={24} lg={24} xs={24}>
          <Form layout="vertical" {...formProps}>
            <Form.Item
              label={
                <Space>
                  メモ
                  {autoSaveProps.status === 'loading' && <Spin />}
                </Space>
              }
              name="memo"
            >
              <Input.TextArea autoSize />
            </Form.Item>
          </Form>
        </Col>
      </Row>
    </Modal>
  );
};
